import React, { useEffect, useState, useCallback } from 'react';
import { adminApi } from '../utils/api';
import { Card, Table, TR, TD, Badge, Btn, PageHeader, Pagination, fmtDate } from '../components/UI';
import RocketLoader from '../components/RocketLoader';

const TYPE_ICONS  = { resume: '📄', cover_letter: '✉️', cv: '🎓' };
const TYPE_LABELS = { resume: 'Resume', cover_letter: 'Cover Letter', cv: 'Academic CV' };

export const DocumentsPage = () => {
  const [docs,    setDocs]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [pages,   setPages]   = useState(1);
  const [page,    setPage]    = useState(1);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setType] = useState('');
  const [search, setSearch]   = useState('');
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fn = search.trim() ? adminApi.searchDocuments : adminApi.getDocuments;
      const data = await fn({ page, limit: 20, type: typeFilter, q: search.trim() });
      setDocs(data.docs);
      setTotal(data.total);
      setPages(data.pages);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, typeFilter, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [typeFilter, search]);

  const handleDelete = async (id) => {
    if (!window.confirm('Permanently delete this document? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await adminApi.deleteDocument(id);
      setDocs(prev => prev.filter(d => d._id !== id));
      setTotal(t => t - 1);
    } catch (e) { alert(e.message); }
    finally { setDeleting(null); }
  };

  const typeColors = { resume: 'blue', cover_letter: 'green', cv: 'purple' };

  return (
    <div>
      <PageHeader
        title={`Documents (${total})`}
        subtitle="Browse and manage all user-created documents"
      />

      {/* Filters */}
      <Card style={{ marginBottom: 16, padding: '14px 16px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by owner name or email…"
            style={{ flex: 1, minWidth: 200, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 7, padding: '8px 12px', color: '#0F172A', fontSize: 13, outline: 'none' }} />
          <span style={{ color: '#64748B', fontSize: 13, flexShrink: 0 }}>Type:</span>
          {[['', 'All'], ['resume', '📄 Resume'], ['cover_letter', '✉️ Cover Letter'], ['cv', '🎓 CV']].map(([v, l]) => (
            <button key={v} onClick={() => setType(v)}
              style={{ background: typeFilter === v ? '#EF1D28' : '#F8FAFC', color: typeFilter === v ? 'white' : '#64748B', border: `1px solid ${typeFilter === v ? '#EF1D28' : '#E2E8F0'}`, borderRadius: 20, padding: '4px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
              {l}
            </button>
          ))}
          <span style={{ marginLeft: 'auto', color: '#64748B', fontSize: 12, flexShrink: 0 }}>{total} total</span>
        </div>
      </Card>

      <Card style={{ padding: 0 }}>
        <Table headers={['Document', 'Type', 'Template', 'Owner', 'Plan', 'Updated', 'Action']}>
          {loading ? (
            <TR><TD><div style={{ padding: 30, display: 'flex', justifyContent: 'center' }}><RocketLoader size={120} message="Loading documents…" /></div></TD></TR>
          ) : docs.length === 0 ? (
            <TR><TD colSpan={7}><div style={{ padding: 30, color: '#64748B', textAlign: 'center' }}>No documents found</div></TD></TR>
          ) : docs.map(doc => (
            <TR key={doc._id}>
              <TD>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 18 }}>{TYPE_ICONS[doc.type] || '📄'}</span>
                  <div>
                    <div style={{ color: '#0F172A', fontWeight: 600, fontSize: 13 }}>
                      {doc.data?.personalInfo?.fullName || doc.title || 'Untitled'}
                    </div>
                    <div style={{ color: '#64748B', fontSize: 11 }}>
                      {doc.data?.personalInfo?.jobTitle || doc.data?.jobInfo?.role || '—'}
                    </div>
                  </div>
                </div>
              </TD>
              <TD><Badge color={typeColors[doc.type] || 'gray'}>{TYPE_LABELS[doc.type] || doc.type}</Badge></TD>
              <TD style={{ color: '#64748B', fontSize: 12, textTransform: 'capitalize' }}>{doc.template || 'modern'}</TD>
              <TD>
                {doc.userId ? (
                  <div>
                    <div style={{ color: '#334155', fontSize: 13 }}>{doc.userId.name}</div>
                    <div style={{ color: '#64748B', fontSize: 11 }}>{doc.userId.email}</div>
                  </div>
                ) : <span style={{ color: '#CBD5E1', fontSize: 12 }}>Deleted user</span>}
              </TD>
              <TD>
                {doc.userId && (
                  <Badge color={doc.userId.plan === 'pro' ? 'purple' : 'gray'}>
                    {doc.userId.plan === 'pro' ? '⭐ Pro' : 'Free'}
                  </Badge>
                )}
              </TD>
              <TD style={{ color: '#64748B', fontSize: 12 }}>{fmtDate(doc.updatedAt)}</TD>
              <TD>
                <button onClick={() => handleDelete(doc._id)} disabled={deleting === doc._id}
                  style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA', borderRadius: 7, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', opacity: deleting === doc._id ? 0.5 : 1 }}>
                  {deleting === doc._id ? '…' : 'Delete'}
                </button>
              </TD>
            </TR>
          ))}
        </Table>
      </Card>

      <Pagination page={page} pages={pages} onChange={setPage} />
    </div>
  );
};
